import React, { useState, useEffect, useRef } from 'react';
import { Calculator, ShoppingCart, Zap, Target, Users, Video, QrCode, Star, Package, Gift, RefreshCw, Shield, BarChart3, UserPlus, Image as ImageIcon, Info, TrendingUp, Clock, Users as UsersIcon, ChevronDown, Download } from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { QuotePDF } from './pdf';
import { ReportingQuotePDF } from './pdf/ReportingQuotePDF';

interface CatalogItem {
  id: string;
  name: string;
  description: string;
  setupPrice: number;
  recurringPrice: number;
  unit: string;
  category: 'placement' | 'awareness' | 'engagement';
  icon: React.ReactNode;
  image?: string;
  unitType: string;
  chartData?: {
    metrics: Array<{
      label: string;
      value: number | string;
      unit: string;
      color: string;
      icon: React.ReactNode;
    }>;
    timeframe: string;
  };
}

interface PhysicalSkuTier {
  id: string;
  name: string;
  skus: number;
  machines: number;
  description: string;
  setupCost: number;
  recurringCost: number;
}

interface LocationPricing {
  id: string;
  name: string;
  setupMultiplier: number;
  recurringMultiplier: number;
  description: string;
}

// Allow numeric inputs to temporarily be empty strings in UI
type BlankableNumber = number | '';

// Shape used when exporting selected items to PDF
type SelectedItemEntry = {
  name: string;
  description: string;
  quantity: string;
  setupPrice: number;
  recurringPrice: number;
  unit: string;
};

const locationPricingTiers: LocationPricing[] = [
  {
    id: 'manchester-airport',
    name: 'Manchester Airport Group',
    setupMultiplier: 1,
    recurringMultiplier: 1,
    description: '~110 Machines'
  }
];

// PLACEHOLDER: Define your physical SKU tiers here
const physicalSkuTiers: PhysicalSkuTier[] = [
  {
    id: 'tier-1',
    name: 'Tier 1',
    skus: 1,
    machines: 0,
    description: 'Define your tier description',
    setupCost: 0,
    recurringCost: 0
  }
  // Add more tiers as needed
];

// PLACEHOLDER: Define your catalog items here with actual pricing
const catalogItems: CatalogItem[] = [
  {
    id: 'physical-sku',
    name: 'Physical SKU Listing',
    description: 'Define your product description',
    setupPrice: 0, // TODO: Set your price // TODO: Set your setup price
    recurringPrice: 0, // TODO: Set your price // TODO: Set your recurring price
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Package className="w-6 h-6" />,
    image: '/calculator-images/1.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Metric 1', value: 0, unit: '%', color: 'bg-blue-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Metric 2', value: 0, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'dooh-ad',
    name: 'DOOH Ad | 1 Slot/60s',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/2.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Brand Awareness Lift', value: 22, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> },
        { label: 'Total Impressions', value: '~37,500', unit: '/month/machine', color: 'bg-pink-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'home-banner',
    name: 'Home Banner Ad',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/3.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Brand Awareness Uplift', value: 12, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-icon-lead',
    name: 'Category Icon Lead',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'awareness',
    icon: <Zap className="w-6 h-6" />,
    image: '/calculator-images/4.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 12, unit: '%', color: 'bg-orange-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-banner',
    name: 'Category Banner Ad',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/5.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share Increase', value: 8, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'premium-listing',
    name: 'Premium Product Listing',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/6.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 18, unit: '%', color: 'bg-blue-500', icon: <ShoppingCart className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'exclusive-category',
    name: 'Exclusive Category',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Category Per Machine',
    category: 'placement',
    icon: <Shield className="w-6 h-6" />,
    image: '/calculator-images/7.png',
    unitType: 'Categories',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Market Share', value: 6, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 15, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'enhanced-detail',
    name: 'Enhanced Product Detail',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'engagement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/8.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 1.5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Conversion Rate', value: 10, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'vend-video',
    name: 'Vend Video Ad',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Video className="w-6 h-6" />,
    image: '/calculator-images/9.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Captive Audience', value: 100, unit: '%', color: 'bg-green-500', icon: <Video className="w-4 h-4" /> },
        { label: 'Brand Awareness Lift', value: 6, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'virtual-sku',
    name: 'Virtual SKU Listing',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Package className="w-6 h-6" />,
    image: '/calculator-images/10.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Conversion Rate', value: '~8', unit: '%', color: 'bg-green-500', icon: <ShoppingCart className="w-4 h-4" /> },
        { label: 'Retention Rate', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'bundle-sku',
    name: 'Bundle SKU Listing',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Gift className="w-6 h-6" />,
    image: '/calculator-images/11.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Opt In Rate', value: 8, unit: '%', color: 'bg-green-500', icon: <ShoppingCart className="w-4 h-4" /> },
        { label: 'Intent to Purchase', value: 80, unit: '%', color: 'bg-blue-500', icon: <Gift className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'subscription-sku',
    name: 'Subscription SKU Listing',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <RefreshCw className="w-6 h-6" />,
    image: '/calculator-images/12.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Subscription Conversion', value: 4, unit: '%', color: 'bg-green-500', icon: <RefreshCw className="w-4 h-4" /> },
        { label: 'Customer Retention', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'safe-qr',
    name: 'SafeQR Redirect',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price // £5 + £2 per redirect (simplified as base rate)
    unit: 'Per QR Per Machine',
    category: 'engagement',
    icon: <QrCode className="w-6 h-6" />,
    image: '/calculator-images/13.png',
    unitType: 'QR Codes',
    chartData: {
      metrics: [],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-survey',
    name: 'Customer Survey',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price // £3 + £5 per survey (simplified as base rate)
    unit: 'Per Survey Per Machine',
    category: 'engagement',
    icon: <BarChart3 className="w-6 h-6" />,
    image: '/calculator-images/14.png',
    unitType: 'Surveys',
    chartData: {
      metrics: [
        { label: 'Survey Responses', value: 850, unit: '+', color: 'bg-green-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Completion Rate', value: 52, unit: '%', color: 'bg-blue-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-signup',
    name: 'Customer Sign-Up',
    description: '',
    setupPrice: 0, // TODO: Set your price
    recurringPrice: 0, // TODO: Set your price
    unit: 'Per Slot Per Machine',
    category: 'engagement',
    icon: <UserPlus className="w-6 h-6" />,
    image: '/calculator-images/15.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Opt-In Rate', value: 20, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> },
        { label: 'Email Marketing ROI', value: 38, unit: '%', color: 'bg-purple-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  }
];

const PricingCalculator: React.FC = () => {
  const [showCustomTabs, setShowCustomTabs] = useState(false);
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: { machines: BlankableNumber; units: BlankableNumber } }>({});
  const [selectedPhysicalTier, setSelectedPhysicalTier] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('manchester-airport');
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<4 | 8 | 12>(4);
  const [totalSetupPrice, setTotalSetupPrice] = useState(0);
  const [totalRecurringPrice, setTotalRecurringPrice] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  // Reporting and Analytics state
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [selectedMachineBracket, setSelectedMachineBracket] = useState<string>('1-25');
  const [selectedSkuTier, setSelectedSkuTier] = useState<string>('1-10');
  const [isMachineDropdownOpen, setIsMachineDropdownOpen] = useState(false);
  const [isSkuDropdownOpen, setIsSkuDropdownOpen] = useState(false);
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [isLiveReportingEnabled, setIsLiveReportingEnabled] = useState(false);
  
  // Pricing calculation function
  const calculatePricing = (tier: string | null, machineBracket: string, skuTier: string, liveReporting: boolean = false) => {
    if (!tier) {
      return { total: 0, basePrice: 0, skuAddOn: 0, liveReportingAddOn: 0, machineBracket: machineBracket };
    }
    const pricing = {
      'basic': { '1-25': 250, '26-100': 500, '101-250': 750, '251+': 1000 },
      'professional': { '1-25': 500, '26-100': 1000, '101-250': 1500, '251+': 2000 },
      'enterprise': { '1-25': 1000, '26-100': 2000, '101-250': 3000, '251+': 4000 }
    };
    
    const basePrice = pricing[tier as keyof typeof pricing][machineBracket as keyof typeof pricing['basic']];
    let skuAddOn = 0;
    
    if (skuTier === '11-20') skuAddOn = 500;
    else if (skuTier === '21+') return { total: 'Custom', basePrice, skuAddOn: 'Contact Sales', liveReportingAddOn: 0, machineBracket, skuTier };
    
    // Enterprise tier always requires custom pricing
    if (tier === 'enterprise') {
      return { total: 'Custom', basePrice: 'Contact Sales', skuAddOn: 'Contact Sales', liveReportingAddOn: 'Contact Sales', machineBracket, skuTier };
    }
    
    // Live reporting add-on pricing
    let liveReportingAddOn = 0;
    if (liveReporting) {
      if (tier === 'basic') liveReportingAddOn = 250;
      else if (tier === 'professional') liveReportingAddOn = 750;
    }
    
    const total = basePrice + skuAddOn + liveReportingAddOn;
    return { total, basePrice, skuAddOn, liveReportingAddOn, machineBracket, skuTier };
  };
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  // Disabled to prevent scroll jitter
  // const [isScrolled, setIsScrolled] = useState(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState<string | null>(null);

  // Contact information for PDF export
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    email: '',
    company: '',
    date: new Date().toISOString().split('T')[0] // Default to today's date
  });





  // Optimized PDF export component that only creates PDF when clicked
  const ExportPDFButton = ({ selectedItems, selectedLocation, contactInfo, totalSetupPrice, totalRecurringPrice, catalogItems, getCurrentPricing, getLocationName, selectedTimePeriod }: any) => {
    const [isGenerating, setIsGenerating] = useState(false);

    const handleExport = async () => {
      setIsGenerating(true);
      try {
        const selectedItemsData = Object.entries(selectedItems)
          .filter(([, cfg]: any) => (cfg.machines || 0) > 0 && (cfg.units || 0) > 0)
          .map(([itemId, cfg]: any) => {
            const item = catalogItems.find((i: any) => i.id === itemId)!;
            const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
            
            // Special handling for physical SKU - use tier-specific pricing
            let setupPrice, recurringPrice;
            if (itemId === 'physical-sku' && selectedPhysicalTier) {
              const tier = physicalSkuTiers.find(t => t.id === selectedPhysicalTier);
              setupPrice = tier ? Math.round(tier.setupCost * currentLocationPricing.setupMultiplier) : 0;
              // Physical SKU recurring cost stays the same regardless of time period
              recurringPrice = tier ? Math.round(tier.recurringCost * currentLocationPricing.recurringMultiplier) : 0;
            } else {
              setupPrice = getCurrentPricing(item).setupPrice;
              // For non-Physical-SKU items, apply time multiplier to recurring cost
              recurringPrice = getCurrentPricing(item).recurringPrice * (selectedTimePeriod / 4);
            }
            
            return {
              id: item.id,
              name: item.name,
              unitType: item.unitType,
              unit: item.unit,
              setupPrice: setupPrice,
              recurringPrice: recurringPrice,
              machines: cfg.machines as number,
              units: cfg.units as number,
              image: `${window.location.origin}${item.image}`
            };
          });

        const doc = (
          <QuotePDF
            locationName={getLocationName(selectedLocation)}
            contact={{ fullName: contactInfo.fullName, email: contactInfo.email, company: contactInfo.company }}
            items={selectedItemsData}
            totals={{ setup: totalSetupPrice, recurring: totalRecurringPrice }}
            timePeriod={selectedTimePeriod}
          />
        );

        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Bright-Blue-Quote-${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error generating PDF:', error);
        alert('Error generating PDF. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    };

    return (
      <button
        onClick={handleExport}
        disabled={isGenerating}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg transition-colors duration-200 font-medium"
      >
        <Download className="w-4 h-4" />
        {isGenerating ? 'Generating PDF…' : 'Export Quote as PDF'}
      </button>
    );
  };




  const enableCustomMode = () => {
    setShowCustomTabs(true);
    setSelectedCategory('all');
  };

  const getLocationName = (location: string) => {
    const tier = locationPricingTiers.find(t => t.id === location);
    return tier ? tier.name : 'Manchester Airport Group';
  };

  // Disabled collapsing header to prevent scroll jitter
  // useEffect(() => {
  //   let ticking = false;
  //   
  //   const handleScroll = () => {
  //     if (!ticking) {
  //       ticking = true;
  //       requestAnimationFrame(() => {
  //         const scrollTop = window.scrollY;
  //         
  //         // Use hysteresis to prevent rapid toggling
  //         // Account for AppHeader height (56px)
  //         if (!isScrolled && scrollTop > 180) {
  //           setIsScrolled(true);
  //         } else if (isScrolled && scrollTop < 100) {
  //           setIsScrolled(false);
  //         }
  //         
  //         ticking = false;
  //       });
  //     }
  //   };
  //
  //   window.addEventListener('scroll', handleScroll, { passive: true });
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, [isScrolled]);

  useEffect(() => {
    const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
    
    const setupTotal = Object.entries(selectedItems).reduce((sum, [itemId, config]) => {
      const item = catalogItems.find(i => i.id === itemId);
      const machines = config.machines === '' ? 0 : config.machines;
      const units = config.units === '' ? 0 : config.units;
      
      // Special handling for physical SKU - use tier-specific pricing
      if (itemId === 'physical-sku' && selectedPhysicalTier) {
        const tier = physicalSkuTiers.find(t => t.id === selectedPhysicalTier);
        return sum + (tier ? Math.round(tier.setupCost * currentLocationPricing.setupMultiplier) : 0);
      }
      
      return sum + (item && machines > 0 && units > 0 ? item.setupPrice * currentLocationPricing.setupMultiplier * units : 0);
    }, 0);
    
    const recurringTotal = Object.entries(selectedItems).reduce((sum, [itemId, config]) => {
      const item = catalogItems.find(i => i.id === itemId);
      const machines = config.machines === '' ? 0 : config.machines;
      const units = config.units === '' ? 0 : config.units;
      
      // Special handling for physical SKU - use tier-specific pricing (no time multiplier)
      if (itemId === 'physical-sku' && selectedPhysicalTier) {
        const tier = physicalSkuTiers.find(t => t.id === selectedPhysicalTier);
        return sum + (tier ? Math.round(tier.recurringCost * currentLocationPricing.recurringMultiplier) : 0);
      }
      
      // For all other items, apply time period multiplier
      const baseRecurring = item ? item.recurringPrice * currentLocationPricing.recurringMultiplier * machines * units : 0;
      return sum + (baseRecurring * (selectedTimePeriod / 4));
    }, 0);
    
    setTotalSetupPrice(setupTotal);
    setTotalRecurringPrice(recurringTotal);
  }, [selectedItems, selectedLocation, selectedPhysicalTier, selectedTimePeriod]);

  const handleMachineChange = (itemId: string, machines: BlankableNumber) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        machines: machines === '' ? '' : machines,
        units: prev[itemId]?.units || 0
      }
    }));
  };

  const handleUnitChange = (itemId: string, units: BlankableNumber) => {
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        machines: prev[itemId]?.machines || 0,
        units: units === '' ? '' : units
      }
    }));
  };

  const handlePhysicalTierChange = (tierId: string) => {
    setSelectedPhysicalTier(tierId);
    if (tierId) {
      const tier = physicalSkuTiers.find(t => t.id === tierId);
      if (tier) {
        setSelectedItems(prev => ({
          ...prev,
          'physical-sku': {
            machines: tier.machines,
            units: tier.skus
          }
        }));
      }
    } else {
      setSelectedItems(prev => ({
        ...prev,
        'physical-sku': { machines: 0, units: 0 }
      }));
    }
  };

  const filteredItems = selectedCategory === 'all' 
    ? catalogItems 
    : catalogItems.filter(item => item.category === selectedCategory);

  const retailMediaOptions = [
    { id: 'all', name: 'All Items', icon: <Calculator className="w-4 h-4" /> },
    { id: 'browse', name: 'Visual Catalogue', icon: <ImageIcon className="w-4 h-4" /> },
    { id: 'placement', name: 'Product Placement & Listings', icon: <Package className="w-4 h-4" /> },
    { id: 'awareness', name: 'Brand Awareness & Visibility', icon: <Target className="w-4 h-4" /> },
    { id: 'engagement', name: 'Engagement & Insights', icon: <BarChart3 className="w-4 h-4" /> }
  ];

  const getTotalQuantity = (itemId: string) => {
    const config = selectedItems[itemId];
    if (!config) return 0;
    const machines = config.machines === '' ? 0 : config.machines;
    const units = config.units === '' ? 0 : config.units;
    return machines * units;
  };

  const isSelected = (itemId: string) => {
    return getTotalQuantity(itemId) > 0;
  };

  const getCurrentPricing = (item: CatalogItem) => {
    const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
    return {
      setupPrice: Math.round(item.setupPrice * currentLocationPricing.setupMultiplier),
      recurringPrice: Math.round(item.recurringPrice * currentLocationPricing.recurringMultiplier)
    };
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 py-3">
        <div className="w-full px-2 md:px-4 lg:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 rounded-lg p-2">
                <Calculator className="text-white w-7 h-7" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">Commerce and Media Calculator</p>
              </div>
            </div>
            {selectedCategory !== 'reporting' && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-2">
              <div className="flex gap-2 mb-2">
                  {[4, 8, 12].map((weeks) => (
                    <button
                      key={weeks}
                      onClick={() => setSelectedTimePeriod(weeks as 4 | 8 | 12)}
                      className={`px-2 py-1 rounded-md text-xs font-semibold transition-colors ${
                        selectedTimePeriod === weeks
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      {weeks} Weeks
                    </button>
                  ))}
                </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-blue-900">Setup:</span>
                <span className="font-bold text-blue-900">£{totalSetupPrice.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="font-bold text-blue-900 mr-2">{selectedTimePeriod} Week Cost:</span>
                <span className="font-bold text-blue-900">£{totalRecurringPrice.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2 border-t border-blue-300 mt-1">
                <span className="font-bold text-blue-900">Total:</span>
                <span className="font-bold text-blue-900">£{(totalSetupPrice + totalRecurringPrice).toLocaleString()}</span>
              </div>
            </div>
            )}
            
            {selectedCategory === 'reporting' && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-2">
                <div className="text-sm text-blue-600 font-medium mb-1">Monthly Cost</div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-blue-900">Reporting:</span>
                  <span className="font-bold text-blue-900 ml-2">
                    {(() => {
                      const pricing = calculatePricing(selectedTier, selectedMachineBracket, selectedSkuTier, isLiveReportingEnabled);
                      if (!selectedTier) return 'Select Tier';
                      return pricing.total === 'Custom' ? 'Contact Sales' : `£${pricing.total.toLocaleString()}`;
                    })()}
                  </span>
          </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-bold text-blue-900">Tier:</span>
                  <span className="font-bold text-blue-900 ml-2">
                    {!selectedTier ? 'Select Tier' :
                     selectedTier === 'basic' ? 'Basic' : 
                     selectedTier === 'professional' ? 'Professional' : 
                     'Enterprise'}
                  </span>
                </div>
                <div className="text-xs text-blue-600 mt-1 pt-1 border-t border-blue-200">
                    {selectedMachineBracket} machines • {selectedSkuTier} SKUs
                    {isLiveReportingEnabled && (
                      <span className="flex items-center space-x-1 ml-2">
                        <div className="relative">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                          <div className="absolute inset-0 w-1.5 h-1.5 bg-green-500 rounded-full animate-ping opacity-75"></div>
                        </div>
                        <span className="text-green-600 font-semibold">LIVE</span>
                      </span>
                    )}
                  </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="w-full px-2 md:px-4 lg:px-6 py-4 md:py-8">
        {/* Navigation */}
        <div className="mb-8">
          <div className="flex gap-2 items-center overflow-x-auto pb-2">
            {/* Retail Media Button */}
            <button
              onClick={() => setSelectedCategory('all')}
              className={`flex items-center space-x-3 px-8 py-4 rounded-lg font-semibold text-xl transition-all duration-200 flex-shrink-0 ${
                selectedCategory === 'all' || selectedCategory === 'browse' || selectedCategory === 'placement' || selectedCategory === 'awareness' || selectedCategory === 'engagement'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              <Calculator className="w-6 h-6" />
              <span>Retail Media</span>
            </button>

            {/* Reporting and Analytics Button */}
            <button
              onClick={() => setSelectedCategory('reporting')}
              className={`flex items-center space-x-3 px-8 py-4 rounded-lg font-semibold text-xl transition-all duration-200 flex-shrink-0 ${
                selectedCategory === 'reporting'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
              }`}
            >
              <BarChart3 className="w-6 h-6" />
              <span>Reporting & Analytics</span>
              <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full ml-2 font-semibold">NEW</span>
            </button>
          </div>
        </div>

        {/* Category Filter - Only shown when in Retail Media Calculator */}
        {selectedCategory !== 'reporting' && (
          <div className="mb-8">
            <div className="flex gap-2 items-center overflow-x-auto pb-2">
              {retailMediaOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => setSelectedCategory(option.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 flex-shrink-0 ${
                    selectedCategory === option.id
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                  }`}
                >
                  {option.icon}
                  <span>{option.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reporting and Analytics Calculator */}
        {/* Reporting & Analytics Section */}
        {selectedCategory === 'reporting' && (
          <div className="space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Reporting & Analytics Pricing</h2>
              <p className="text-gray-600 text-lg">Get insights and analytics for your retail media campaigns</p>
            </div>

            {/* Step 1: Tier Selection */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Step 1: Select Your Reporting Tier</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Basic Tier */}
                <div 
                  className={`rounded-xl p-6 shadow-lg cursor-pointer transition-all duration-300 border-2 flex flex-col h-full ${
                    selectedTier === 'basic' 
                      ? 'bg-blue-100 border-blue-300 scale-105 shadow-xl' 
                      : 'bg-white border-gray-200 hover:border-blue-400 hover:scale-102 hover:shadow-xl hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    setSelectedTier('basic');
                    if (!hasAutoScrolled) {
                      setHasAutoScrolled(true);
                      setTimeout(() => {
                        document.getElementById('step-2-section')?.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }, 100);
                    }
                  }}
                >
                  <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 ${
                      selectedTier === 'basic' ? 'text-blue-900' : 'text-gray-900'
                    }`}>Basic</h3>
                    <p className={`text-sm ${
                      selectedTier === 'basic' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Aggregated metrics, real-time dashboard, PDF reports</p>
                          </div>
                  
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'basic' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'basic' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'basic' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Total monthly sales in £</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'basic' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'basic' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'basic' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Total monthly sales in units</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'basic' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'basic' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'basic' ? 'text-blue-800' : 'text-gray-700'
                      }`}>5 CSV exports</span>
                    </div>
                  </div>

                  <div className={`border-t pt-4 text-center mt-6 ${
                    selectedTier === 'basic' ? 'border-blue-300' : 'border-gray-200'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      selectedTier === 'basic' ? 'text-blue-900' : 'text-gray-900'
                    }`}>From £250</div>
                    <div className={`text-sm ${
                      selectedTier === 'basic' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Per Month</div>
                  </div>
                </div>

                {/* Professional Tier */}
                <div 
                  className={`rounded-xl p-6 shadow-lg cursor-pointer transition-all duration-300 border-2 flex flex-col h-full ${
                    selectedTier === 'professional' 
                      ? 'bg-blue-100 border-blue-300 scale-105 shadow-xl' 
                      : 'bg-white border-gray-200 hover:border-blue-400 hover:scale-102 hover:shadow-xl hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    setSelectedTier('professional');
                    if (!hasAutoScrolled) {
                      setHasAutoScrolled(true);
                      setTimeout(() => {
                        document.getElementById('step-2-section')?.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }, 100);
                    }
                  }}
                >
                  <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 ${
                      selectedTier === 'professional' ? 'text-blue-900' : 'text-gray-900'
                    }`}>Professional</h3>
                    <p className={`text-sm ${
                      selectedTier === 'professional' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Segmented metrics, alerts, interactive reports</p>
                  </div>
                  
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Everything in Basic</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Total monthly sales per SKU</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Total monthly sales per machine</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Planogram availability</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Distribution by machine and gym</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Sales by time and region</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Total and category market share</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Conversion rate per SKU</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'professional' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'professional' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'professional' ? 'text-blue-800' : 'text-gray-700'
                      }`}>10 CSV exports</span>
                    </div>
                  </div>
                  
                  <div className={`border-t pt-4 text-center mt-6 ${
                    selectedTier === 'professional' ? 'border-blue-300' : 'border-gray-200'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      selectedTier === 'professional' ? 'text-blue-900' : 'text-gray-900'
                    }`}>From £500</div>
                    <div className={`text-sm ${
                      selectedTier === 'professional' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Per Month</div>
                  </div>
                </div>

                {/* Enterprise Tier */}
                <div 
                  className={`rounded-xl p-6 shadow-lg cursor-pointer transition-all duration-300 border-2 flex flex-col h-full ${
                    selectedTier === 'enterprise' 
                      ? 'bg-blue-100 border-blue-300 scale-105 shadow-xl' 
                      : 'bg-white border-gray-200 hover:border-blue-400 hover:scale-102 hover:shadow-xl hover:bg-blue-50'
                  }`}
                  onClick={() => {
                    setSelectedTier('enterprise');
                    if (!hasAutoScrolled) {
                      setHasAutoScrolled(true);
                      setTimeout(() => {
                        document.getElementById('step-2-section')?.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }, 100);
                    }
                  }}
                >
                  <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 ${
                      selectedTier === 'enterprise' ? 'text-blue-900' : 'text-gray-900'
                    }`}>Enterprise</h3>
                    <p className={`text-sm ${
                      selectedTier === 'enterprise' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Custom insights, predictive analytics, unlimited reports</p>
                  </div>
                  
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>A/B testing</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Price elasticity testing</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Vision and demographic reporting</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Heat map and behavior tracking</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Funnel and conversion reporting</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        selectedTier === 'enterprise' ? 'bg-blue-200' : 'bg-gray-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          selectedTier === 'enterprise' ? 'bg-blue-600' : 'bg-gray-600'
                        }`}></div>
                      </div>
                      <span className={`text-sm ${
                        selectedTier === 'enterprise' ? 'text-blue-800' : 'text-gray-700'
                      }`}>Additional custom analytics</span>
                    </div>
                  </div>
                  
                  <div className={`border-t pt-4 text-center mt-6 ${
                    selectedTier === 'enterprise' ? 'border-blue-300' : 'border-gray-200'
                  }`}>
                    <div className={`text-2xl font-bold ${
                      selectedTier === 'enterprise' ? 'text-blue-900' : 'text-gray-900'
                    }`}>Contact Sales</div>
                    <div className={`text-sm ${
                      selectedTier === 'enterprise' ? 'text-blue-700' : 'text-gray-600'
                    }`}>Custom Pricing</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
                {/* Step 2: Number of Machines */}
                <div id="step-2-section" className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Step 2: Number of Machines</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Machine Count Bracket
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsMachineDropdownOpen(!isMachineDropdownOpen)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white flex items-center justify-between hover:border-gray-400 transition-colors"
                        >
                          <span className="text-gray-900">
                            {selectedMachineBracket === '1-25' && '1-25 machines (£250)'}
                            {selectedMachineBracket === '26-100' && '26-100 machines (£500)'}
                            {selectedMachineBracket === '101-250' && '101-250 machines (£750)'}
                            {selectedMachineBracket === '251+' && '251+ machines (£1,000)'}
                          </span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isMachineDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isMachineDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            {[
                              { value: '1-25', label: '1-25 machines' },
                              { value: '26-100', label: '26-100 machines' },
                              { value: '101-250', label: '101-250 machines' },
                              { value: '251+', label: '251+ machines' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setSelectedMachineBracket(option.value);
                                  setIsMachineDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                  selectedMachineBracket === option.value ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">
                        {selectedMachineBracket === '1-25' && 'Selected: 1-25 machines (£250)'}
                        {selectedMachineBracket === '26-100' && 'Selected: 26-100 machines (£500)'}
                        {selectedMachineBracket === '101-250' && 'Selected: 101-250 machines (£750)'}
                        {selectedMachineBracket === '251+' && 'Selected: 251+ machines (£1,000)'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step 3: Number of SKUs */}
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Step 3: Number of SKUs</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SKU Count Tier
                      </label>
                      <div className="relative">
                        <button
                          onClick={() => setIsSkuDropdownOpen(!isSkuDropdownOpen)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg bg-white flex items-center justify-between hover:border-gray-400 transition-colors"
                        >
                          <span className="text-gray-900">
                            {selectedSkuTier === '1-10' && '1-10 SKUs (Included)'}
                            {selectedSkuTier === '11-20' && '11-20 SKUs (+£500/month)'}
                            {selectedSkuTier === '21+' && '21+ SKUs (Custom pricing)'}
                          </span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isSkuDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isSkuDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                            {[
                              { value: '1-10', label: '1-10 SKUs (Included)' },
                              { value: '11-20', label: '11-20 SKUs' },
                              { value: '21+', label: '21+ SKUs' }
                            ].map((option) => (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setSelectedSkuTier(option.value);
                                  setIsSkuDropdownOpen(false);
                                }}
                                className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                  selectedSkuTier === option.value ? 'bg-blue-50 text-blue-900' : 'text-gray-900'
                                }`}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">
                        {selectedSkuTier === '1-10' && 'Selected: 1-10 SKUs (Included in base price)'}
                        {selectedSkuTier === '11-20' && 'Selected: 11-20 SKUs (+£500/month add-on)'}
                        {selectedSkuTier === '21+' && 'Selected: 21+ SKUs (Custom pricing required)'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Step 4: Live Reporting */}
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Step 4: Live Reporting</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Enable Live Reporting
                      </label>
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="font-semibold text-gray-900">Real-time Live Reporting</div>
                            {isLiveReportingEnabled && (
                              <div className="flex items-center space-x-1">
                                <div className="relative">
                                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                  <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
                                </div>
                                <span className="text-xs font-bold text-green-600 animate-pulse">LIVE</span>
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            Get instant updates and real-time data streaming for your analytics
                          </div>
                          <div className="text-sm text-blue-600 mt-2 font-medium">
                            {selectedTier === 'basic' && '+£250/month'}
                            {selectedTier === 'professional' && '+£750/month'}
                            {selectedTier === 'enterprise' && 'Contact Sales'}
                            {!selectedTier && 'Select tier to see pricing'}
                          </div>
                        </div>
                        <button
                          onClick={() => setIsLiveReportingEnabled(!isLiveReportingEnabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            isLiveReportingEnabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              isLiveReportingEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">
                        {isLiveReportingEnabled ? 'Live reporting enabled' : 'Live reporting disabled'}
                        {isLiveReportingEnabled && selectedTier && (
                          <span className="text-blue-600 ml-2">
                            {selectedTier === 'basic' && '(+£250/month)'}
                            {selectedTier === 'professional' && '(+£750/month)'}
                            {selectedTier === 'enterprise' && '(Contact Sales)'}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

            {/* Quote Summary at Bottom */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quote Summary</h3>
                  {(() => {
                const pricing = calculatePricing(selectedTier, selectedMachineBracket, selectedSkuTier, isLiveReportingEnabled);
                
                if (pricing.total === 'Custom') {
                      return (
                        <div className="space-y-4">
                          <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <h4 className="font-semibold text-yellow-800 mb-2">Custom Pricing Required</h4>
                            <p className="text-yellow-700 text-sm mb-4">21+ SKUs require custom pricing. Contact our sales team for a tailored quote.</p>
                            <button className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                              Contact Sales
                            </button>
                          </div>
                        </div>
                      );
                    }
                    
                const tierName = selectedTier === 'basic' ? 'Basic' : 
                               selectedTier === 'professional' ? 'Professional' : 
                               'Enterprise';
                    
                    return (
                      <div className="space-y-4">
                        <div className="text-center">
                          {pricing.total === 'Custom' ? (
                            <>
                              <div className="text-4xl font-bold text-orange-600 mb-2">Contact Sales</div>
                              <div className="text-gray-600">Custom pricing required</div>
                            </>
                          ) : (
                            <>
                              <div className="text-4xl font-bold text-blue-600 mb-2">£{pricing.total.toLocaleString()}</div>
                              <div className="text-gray-600">per month</div>
                            </>
                          )}
                        </div>
                        
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Tier:</span>
                          <span className="font-medium">{tierName}</span>
                          </div>
                          <div className="flex justify-between">
                          <span className="text-gray-600">Machine Bracket:</span>
                          <span className="font-medium">{selectedMachineBracket} machines</span>
                          </div>
                          <div className="flex justify-between">
                          <span className="text-gray-600">SKU Tier:</span>
                          <span className="font-medium">{selectedSkuTier} SKUs</span>
                          </div>
                            </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Price:</span>
                          <span className="font-medium">
                            {pricing.basePrice === 'Contact Sales' ? 'Contact Sales' : `£${pricing.basePrice.toLocaleString()}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SKU Add-On:</span>
                          <span className="font-medium">
                            {pricing.skuAddOn === 'Contact Sales' ? 'Contact Sales' :
                             pricing.skuAddOn === 0 ? 'Included' : `+£${pricing.skuAddOn.toLocaleString()}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Live Reporting:</span>
                          <span className="font-medium">
                            {pricing.liveReportingAddOn === 'Contact Sales' ? 'Contact Sales' :
                             pricing.liveReportingAddOn === 0 ? 'Not included' : `+£${pricing.liveReportingAddOn.toLocaleString()}`}
                          </span>
                        </div>
                      </div>
                        </div>
                        
                    {pricing.total === 'Custom' ? (
                      <div className="space-y-3">
                        <button 
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-medium transition-colors"
                          onClick={() => {
                            // Open email client or contact form
                            window.open('mailto:sales@bright.blue?subject=Enterprise Reporting & Analytics Quote Request', '_blank');
                          }}
                        >
                          Contact Sales
                        </button>
                        <div className="text-center text-sm text-gray-600">
                          Enterprise tier requires custom pricing. Our sales team will provide a tailored quote.
                        </div>
                      </div>
                    ) : (
                      <button 
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium transition-colors"
                        onClick={async () => {
                          try {
                            const doc = (
                              <ReportingQuotePDF
                                tierName={tierName}
                                machineCount={parseInt(selectedMachineBracket.split('-')[0])}
                                skuCount={parseInt(selectedSkuTier.split('-')[0])}
                                monthlyCost={pricing.total as number}
                                contact={{ 
                                  fullName: contactInfo.fullName, 
                                  email: contactInfo.email, 
                                  company: contactInfo.company 
                                }}
                              />
                            );

                            const blob = await pdf(doc).toBlob();
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `Bright-Blue-Reporting-Quote-${new Date().toISOString().split('T')[0]}.pdf`;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error generating PDF:', error);
                            alert('Error generating PDF. Please try again.');
                          }
                        }}
                      >
                        Get Quote
                      </button>
                    )}
                      </div>
                    );
                  })()}
                </div>

                {/* Example Scenarios */}
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Example Scenarios</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">Example 1 - 200 machines, 2 SKUs</h4>
                  <p className="text-blue-800 text-sm">Professional Tier, Monthly Fee: £1,500, SKU Add-On: £0, Total: £1,500/month</p>
                    </div>
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Example 2 - 500 machines, 15 SKUs</h4>
                  <p className="text-green-800 text-sm">Enterprise Tier, Monthly Fee: £4,000, SKU Add-On: +£500, Total: £4,500/month</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image Browser */}
        {selectedCategory === 'browse' ? (
          <div className="mb-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Product Image Gallery</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Browse through all product images with detailed views. Click any image to see it in full detail.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
              {catalogItems.filter(item => item.image).map((item, index) => (
                <div
                  key={item.id}
                  className="group relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-xl hover:border-blue-300 transition-all duration-500 cursor-pointer transform hover:-translate-y-2"
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => setSelectedProductDetail(item.id)}
                >
                  {/* Image Container */}
                  <div className="aspect-square overflow-hidden bg-gray-50">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  
                  {/* Overlay with product info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-all duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-8 group-hover:translate-y-0 transition-transform duration-300">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className={`p-1 rounded ${
                          item.category === 'placement' ? 'bg-green-500' :
                          item.category === 'awareness' ? 'bg-blue-500' :
                          item.category === 'engagement' ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`}>
                          {item.icon}
                        </div>
                      </div>
                      <h3 className="font-semibold text-sm mb-1 line-clamp-2">{item.name}</h3>
                      <div className="text-xs opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <div>Setup: £{item.setupPrice.toLocaleString()}</div>
                        <div>4 Weeks: £{item.recurringPrice.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating category badge */}
                  <div className="absolute top-3 right-3">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium text-white ${
                      item.category === 'placement' ? 'bg-green-500' :
                      item.category === 'awareness' ? 'bg-blue-500' :
                      item.category === 'engagement' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`}>
                      {item.category}
                    </div>
                  </div>
                  
                  {/* Hover zoom indicator */}
                  <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Back to products button */}
            <div className="text-center mt-12">
              <button
                onClick={() => setSelectedCategory('all')}
                className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
              >
                <Calculator className="w-5 h-5" />
                <span>Back to Product Calculator</span>
              </button>
            </div>
          </div>
        ) : selectedCategory !== 'reporting' ? (
          /* Items Grid */
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredItems.map(item => (
                <div 
                  key={item.id} 
                  className={`rounded-xl shadow-sm border transition-all duration-300 overflow-hidden cursor-pointer flex ${
                    isSelected(item.id)
                      ? 'bg-blue-200 border-blue-400 shadow-lg text-gray-900'
                      : 'bg-white border-gray-200 hover:shadow-lg hover:border-blue-300'
                  }`}
                  onClick={() => {
                    if (!isSelected(item.id)) {
                      if (item.id === 'physical-sku') {
                        // Auto-select Tier 1A for Physical SKU
                        setSelectedPhysicalTier('tier-1a');
                        const tier1A = physicalSkuTiers.find(t => t.id === 'tier-1a');
                        setSelectedItems(prev => ({
                          ...prev,
                          [item.id]: { 
                            machines: tier1A?.machines || 1, 
                            units: tier1A?.skus || 1 
                          }
                        }));
                      } else {
                        setSelectedItems(prev => ({
                          ...prev,
                          [item.id]: { machines: 1, units: 1 }
                        }));
                      }
                    } else {
                      setSelectedItems(prev => ({
                        ...prev,
                        [item.id]: { machines: 0, units: 0 }
                      }));
                      // Clear tier selection when deselecting Physical SKU
                      if (item.id === 'physical-sku') {
                        setSelectedPhysicalTier('');
                      }
                    }
                  }}
                >
                  {/* Image Container - Left Side */}
                  {item.image && (
                    <div 
                      className="w-32 bg-white overflow-hidden cursor-pointer flex-shrink-0" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProductDetail(item.id);
                      }}
                    >
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Content Container - Right Side */}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          item.category === 'placement' ? 'bg-green-100 text-green-600' :
                          item.category === 'awareness' ? 'bg-blue-100 text-blue-600' :
                          item.category === 'engagement' ? 'bg-purple-100 text-purple-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {item.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductDetail(item.id);
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                      >
                        View Listing
                      </button>
                    </div>

                    <div className="flex items-end justify-between">
                      <div className="text-sm">
                        {item.id === 'physical-sku' && selectedPhysicalTier ? (
                          (() => {
                            const tier = physicalSkuTiers.find(t => t.id === selectedPhysicalTier);
                            const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
                            const setupTotal = Math.round((tier?.setupCost || 0) * currentLocationPricing.setupMultiplier);
                            const recurringTotal = Math.round((tier?.recurringCost || 0) * currentLocationPricing.recurringMultiplier);
                            return (
                              <>
                                <div className="font-bold text-gray-900">Setup: £{setupTotal.toLocaleString()}</div>
                                <div className="font-bold text-gray-900">4-12 Week Cost: £{recurringTotal.toLocaleString()}</div>
                                <div className="text-xs mt-1 text-gray-600">{tier?.machines} machines × {tier?.skus} SKU{tier?.skus && tier.skus > 1 ? 's' : ''}</div>
                              </>
                            );
                          })()
                        ) : (
                          <>
                            <div className="font-bold text-gray-900">Setup: £{getCurrentPricing(item).setupPrice.toLocaleString()}</div>
                            <div className="font-bold text-gray-900">4 Weeks: £{getCurrentPricing(item).recurringPrice.toLocaleString()}</div>
                            <div className={`text-xs mt-1 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>{item.unit}</div>
                          </>
                        )}
                      </div>
                      {/* Physical SKU Dropdown or Regular Controls */}
                      {item.id === 'physical-sku' ? (
                        <div className="flex flex-col space-y-2 ml-4">
                          <select
                            value={selectedPhysicalTier}
                            onChange={(e) => {
                              e.stopPropagation();
                              handlePhysicalTierChange(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white min-w-[200px]"
                          >
                            {physicalSkuTiers.map(tier => (
                              <option key={tier.id} value={tier.id}>
                                {tier.name} - {tier.skus} SKU{tier.skus > 1 ? 's' : ''} / {tier.machines} Machines
                              </option>
                            ))}
                          </select>
                          {selectedPhysicalTier && (
                            <div className="text-xs text-gray-600 mt-1 max-w-[200px] leading-relaxed">
                              {physicalSkuTiers.find(t => t.id === selectedPhysicalTier)?.description}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col space-y-2">
                          {/* Machines Control */}
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs w-16 ${isSelected(item.id) ? 'text-gray-600' : 'text-gray-500'}`}>Machines:</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMachineChange(item.id, (selectedItems[item.id]?.machines || 0) - 1);
                              }}
                              className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                              disabled={!selectedItems[item.id]?.machines}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={selectedItems[item.id]?.machines === 0 || selectedItems[item.id]?.machines === '' ? '' : selectedItems[item.id]?.machines || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                const finalValue = value === 0 ? '' : value;
                                handleMachineChange(item.id, finalValue);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="0"
                              className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMachineChange(item.id, (selectedItems[item.id]?.machines || 0) + 1);
                              }}
                              className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                            >
                              +
                            </button>
                          </div>
                          
                          {/* Units Control */}
                          <div className="flex items-center space-x-2">
                            <span className="text-xs w-16 text-gray-500">{item.unitType}:</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) - 1);
                              }}
                              className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                              disabled={!selectedItems[item.id]?.units}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={selectedItems[item.id]?.units === 0 || selectedItems[item.id]?.units === '' ? '' : selectedItems[item.id]?.units || ''}
                              onChange={(e) => {
                                e.stopPropagation();
                                const value = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                                const finalValue = value === 0 ? '' : value;
                                handleUnitChange(item.id, finalValue);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="0"
                              className="w-12 h-6 text-center text-sm font-medium border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnitChange(item.id, (selectedItems[item.id]?.units || 0) + 1);
                              }}
                              className="w-6 h-6 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white transition-colors text-sm"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Image Modal */}
        {expandedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setExpandedImage(null)}
          >
            <div className="relative max-w-xl max-h-[37vh] flex items-center justify-center">
              <img 
                src={expandedImage}
                alt="Expanded view"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setExpandedImage(null)}
                className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Product Detail Modal */}
        {selectedProductDetail && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedProductDetail(null)}
          >
            <div 
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const item = catalogItems.find(i => i.id === selectedProductDetail);
                if (!item) return null;
                
                return (
                  <div>
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          item.category === 'placement' ? 'bg-green-100 text-green-600' :
                          item.category === 'awareness' ? 'bg-blue-100 text-blue-600' :
                          item.category === 'engagement' ? 'bg-purple-100 text-purple-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {item.icon}
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">{item.name}</h2>
                      </div>
                      <button
                        onClick={() => setSelectedProductDetail(null)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                      >
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      {/* Image */}
                      {item.image && (
                        <div className="mb-6">
                          <img 
                            src={item.image} 
                            alt={item.name}
                            className="w-full max-w-md mx-auto rounded-lg shadow-sm border border-gray-200"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Case Study Charts */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">Key Benefits</h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4" />
                            <span>{item.chartData?.timeframe}</span>
                          </div>
                        </div>
                        
                        {item.chartData && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {item.chartData.metrics.map((metric, index) => (
                              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-2">
                                    <div className={`p-2 rounded-lg ${metric.color} text-white`}>
                                      {metric.icon}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">{metric.label}</span>
                                  </div>
                                </div>
                                
                                {/* Progress Bar */}
                                <div className="mb-2">
                                  <div className="flex items-end space-x-2 mb-1">
                                    <span className="text-2xl font-bold text-gray-900">
                                      {typeof metric.value === 'number' && metric.value > 0 && !metric.unit.includes('+') ? '+' : ''}{metric.value}
                                    </span>
                                    <span className="text-lg font-medium text-gray-600 mb-1">{metric.unit}</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${metric.color} transition-all duration-1000 ease-out`}
                                      style={{ 
                                        width: `${Math.min(Math.abs(typeof metric.value === 'number' ? metric.value : 100), 100)}%`,
                                        animationDelay: `${index * 200}ms`
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Pricing Summary */}
                      <div className="mb-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3">Pricing</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-900">£{getCurrentPricing(item).setupPrice.toLocaleString()}</div>
                            <div className="text-sm text-blue-700">Setup Cost</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-900">£{getCurrentPricing(item).recurringPrice.toLocaleString()}</div>
                            <div className="text-sm text-blue-700">4 Week Cost</div>
                          </div>
                        </div>
                        <div className="text-center mt-3 pt-3 border-t border-blue-200">
                          <span className="text-xs text-blue-600">{item.unit}</span>
                        </div>
                      </div>
                      
                      {/* Special handling for Premium Product Listing */}
                      {item.id === 'premium-listing' && (
                        <div className="mb-6">
                          <div className="space-y-4">
                            <p className="text-gray-600">
                              Premium product listing ensures your products get maximum visibility and higher conversion rates.
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <p className="text-blue-800 text-sm">
                                <strong>Note:</strong> Premium product listing guarantees you 1 of the top 6 slots on the machine, read left to right, top to bottom, first come first serve.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Special handling for Physical SKU */}
                      {item.id === 'physical-sku' && (
                        <div className="mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Tiers</h3>
                          <div className="space-y-3">
                            {physicalSkuTiers.map(tier => (
                              <div key={tier.id} className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                                <div className="font-medium text-blue-900">{tier.name}</div>
                                <div className="text-sm text-blue-700">{tier.skus} SKU{tier.skus > 1 ? 's' : ''} across {tier.machines} machines</div>
                                <div className="text-xs text-blue-600 mt-1">{tier.description}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setSelectedProductDetail(null);
                            if (!isSelected(item.id)) {
                              setSelectedItems(prev => ({
                                ...prev,
                                [item.id]: { machines: 1, units: 1 }
                              }));
                            }
                          }}
                          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                            isSelected(item.id)
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                          }`}
                        >
                          {isSelected(item.id) ? 'Already Added' : 'Add to Calculator'}
                        </button>
                        <button
                          onClick={() => setSelectedProductDetail(null)}
                          className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                          Close
                        </button>
                      </div>
                      
                      {/* Disclaimer */}
                      <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-gray-600 leading-relaxed">
                            <strong>Disclaimer:</strong> All figures shown are estimates based on projections and industry benchmarks. Actual results may vary and are not guaranteed. Bright.Blue accepts no liability for differences between projected and actual performance.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Summary */}
        {Object.keys(selectedItems).length > 0 && (
          <div className="mt-12 space-y-6">
            {/* Contact Information Form */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
                              <p className="text-gray-600 text-sm mb-4">Please provide the following information:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    value={contactInfo.fullName}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, fullName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email address"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    value={contactInfo.company}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your company name"
                  />
                </div>

              </div>
            </div>

            {/* Selected Items Summary */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Selected Items
                </h2>
                {Object.entries(selectedItems).filter(([, config]) => {
                  const machinesNum = config.machines === '' ? 0 : config.machines;
                  const unitsNum = config.units === '' ? 0 : config.units;
                  return machinesNum > 0 && unitsNum > 0;
                }).length > 0 && (
                  <ExportPDFButton 
                    selectedItems={selectedItems}
                    selectedLocation={selectedLocation}
                    contactInfo={contactInfo}
                    totalSetupPrice={totalSetupPrice}
                    totalRecurringPrice={totalRecurringPrice}
                    catalogItems={catalogItems}
                    getCurrentPricing={getCurrentPricing}
                    getLocationName={getLocationName}
                    selectedTimePeriod={selectedTimePeriod}
                  />
                )}
              </div>
              <div className="space-y-3">
                {Object.entries(selectedItems)
                  .filter(([, config]) => {
                    const machinesNum = config.machines === '' ? 0 : config.machines;
                    const unitsNum = config.units === '' ? 0 : config.units;
                    return machinesNum > 0 && unitsNum > 0;
                  })
                  .map(([itemId, config]) => {
                    const item = catalogItems.find(i => i.id === itemId);
                    if (!item) return null;
                    const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
                    const machinesNum = config.machines === '' ? 0 : config.machines;
                    const unitsNum = config.units === '' ? 0 : config.units;
                    
                    // Special handling for physical SKU - use tier-specific pricing
                    let totalCost, totalRecurring;
                    if (itemId === 'physical-sku' && selectedPhysicalTier) {
                      const tier = physicalSkuTiers.find(t => t.id === selectedPhysicalTier);
                      totalCost = tier ? Math.round(tier.setupCost * currentLocationPricing.setupMultiplier) : 0;
                      totalRecurring = tier ? Math.round(tier.recurringCost * currentLocationPricing.recurringMultiplier) : 0;
                    } else {
                      const pricing = getCurrentPricing(item);
                      totalCost = pricing.setupPrice * unitsNum;
                      totalRecurring = pricing.recurringPrice * machinesNum * unitsNum;
                    }
                    return (
                      <div key={itemId} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center space-x-3">
                          <div className={`p-1 rounded ${
                            item.category === 'placement' ? 'bg-green-100 text-green-600' :
                            item.category === 'awareness' ? 'bg-blue-100 text-blue-600' :
                            item.category === 'engagement' ? 'bg-purple-100 text-purple-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {item.icon}
                          </div>
                          <span className="font-medium">{item.name}</span>
                          <span className="text-gray-500">({machinesNum} machines × {unitsNum} {item.unitType.toLowerCase()})</span>
                        </div>
                        <span className="font-semibold">
                          Setup: £{totalCost.toLocaleString()} | 
                          4 Weeks: £{totalRecurring.toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Total Setup:</span>
                    <span className="text-blue-600">£{totalSetupPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Total {selectedTimePeriod} Weeks:</span>
                    <span className="text-blue-600">£{totalRecurringPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-gray-300">
                    <span>Total:</span>
                    <span className="text-blue-600">£{(totalSetupPrice + totalRecurringPrice).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingCalculator;